/**
 * STEP EIGHT — "Get rid of the CO₂! It's bad news."
 *
 * First level that asks the student to set up gas exchange from scratch.
 * Three organs in the pool (heart, lungs, diaphragm), and all three are
 * needed for the solution. A full closed circulatory + gas-exchange loop:
 * heart pumps CO₂ → lungs swap CO₂ for O₂ → body absorbs O₂ → body returns
 * CO₂ to heart.
 *
 * Wall ports:
 *   TOP        atmosphere bidirectional   (trachea endpoint)
 *   LEFT-upper receptacle [oxygen]        STRICT — bursts on anything but
 *              pure O₂ (no acceptsAny / acceptsAlso). This is the gate
 *              that gives the lower wall spout permission to fire.
 *   LEFT-lower spout [co2] hideContents   discovery — emits CO₂ but only
 *              after the upper receptacle has received O₂-only blood.
 *              `dependsOn: ['s8-o2-in']`. Same pattern as L6/L7/L9/L10.
 *
 * Intended solution (closed loop, every cone wired):
 *   heart.A-out (always-on CO₂) → lungs.left-in
 *   atmosphere (TOP) ↔ lungs.top (trachea)
 *   diaphragm castellated below lungs (direct link → pulse signal)
 *   lungs.right-out (O₂) → heart.B-in
 *   heart.B-out (O₂) → LEFT-upper (body absorbs O₂ — opens LEFT-lower)
 *   LEFT-lower (CO₂, now firing) → heart.A-in   ← closes the loop
 *
 * Heart.A-out is the unconditional kickstart. The pre-v5.18 loophole
 * (skip the heart entirely; wire s8-co2-out → lungs.left-in directly and
 * the level claimed solved because gated pipes classified CORRECT at t=0
 * without ever flowing) was issue #9, fixed by the engine-level deferred
 * solved check. The strict upper receptacle adds a second safeguard:
 * even if the student wires heart.A-out somewhere wrong, anything other
 * than pure O₂ arriving at s8-o2-in bursts.
 */
import {
  leftReceptacle,
  leftSpout,
  topAtmosphere,
  type LevelConfig,
} from './portHelpers';
import type { MoleculeMix } from '../types';

export const LEVEL_8: LevelConfig = {
  step: 8,
  title: 'STEP EIGHT',
  instructions: "Get RID of the CO₂! It's bad news.",
  // Trachea (atmosphere ↔ lungs top) is mandatory — same loophole as L5: the
  // bidirectional atmosphere could otherwise feed the strict O₂ receptacle
  // directly and bypass the lungs. See LevelConfig.requiredLinks.
  requiredLinks: [{ a: 's8-atmos', b: 's8-lungs-top' }],
  ports: [
    // TOP wall — atmosphere bidirectional (the trachea endpoint).
    topAtmosphere('s8-atmos', 8, ['oxygen']),
    // LEFT upper — strict O₂ receptacle (body receives oxygenated blood).
    // Intentionally NO acceptsAny / acceptsAlso: anything other than
    // pure O₂ classifies UNACCEPTABLE and bursts at this cone.
    leftReceptacle('s8-o2-in', 3, ['oxygen']),
    // LEFT lower — CO₂ spout, discovery (porthole hidden). Fires only
    // after the upper-left receptacle has received O₂-blood (body finished
    // absorbing O₂ → now releases CO₂ back into the heart). Same wall-port
    // `dependsOn` pattern as L6/L7/L9/L10.
    {
      ...leftSpout('s8-co2-out', 13, ['co2']),
      hideContents: true,
      dependsOn: ['s8-o2-in'],
    },
  ],
  organPool: [
    // ---------- Heart (copy of Level 7 heart, s8- prefix) ----------
    // Required: heart.A-out is the unconditional kickstart that pumps
    // CO₂ into the lungs. heart.A-in closes the loop (receives CO₂ back
    // from the body via the lower-left wall spout).
    {
      id: 'heart',
      art: 'heart',
      cols: 4,
      rows: 4,
      activatesWhen: ['s8-heart-A-out', 's8-heart-B-out'],
      ports: [
        {
          id: 's8-heart-A-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 0, dRow: -1 },
          carries: ['co2'],
        },
        {
          id: 's8-heart-A-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 0, dRow: 3 },
          carries: ['co2'],
        },
        {
          id: 's8-heart-B-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 3, dRow: -1 },
          carries: ['oxygen'],
        },
        {
          id: 's8-heart-B-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 3, dRow: 3 },
          carries: ['oxygen'],
          dependsOn: ['s8-heart-B-in'],
        },
      ],
    },
    // ---------- Lungs (copy of Level 5/6 lungs, s8- prefix) ----------
    {
      id: 'lungs',
      art: 'lungs',
      cols: 5,
      rows: 3,
      activatesWhen: ['s8-lungs-right-out'],
      ports: [
        {
          id: 's8-lungs-top',
          kind: 'bidirectional',
          facing: 'N',
          cellOffset: { dCol: 2, dRow: -1 },
          carries: ['oxygen'],
          coneFlipped: true,
          transform: (inputs) => {
            const left = inputs.get('s8-lungs-left-in') ?? [];
            const bottom = inputs.get('s8-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            const tracheaInputArriving = (inputs.get('s8-lungs-top') ?? []).length > 0;
            if (operating && tracheaInputArriving && left.includes('co2')) {
              return ['co2'];
            }
            return null;
          },
        },
        {
          id: 's8-lungs-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['co2'],
          acceptsAny: true,
          validIf: (inputs) => {
            const bottom = inputs.get('s8-lungs-bottom-pulse') ?? [];
            return bottom.includes('pulse');
          },
        },
        {
          id: 's8-lungs-right-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 5, dRow: 0 },
          carries: ['oxygen'],
          transform: (inputs) => {
            const left = inputs.get('s8-lungs-left-in') ?? [];
            const bottom = inputs.get('s8-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            if (!operating) return null;
            if (left.length === 0) return null;
            const tracheaConnected = (inputs.get('s8-lungs-top') ?? []).length > 0;
            if (!tracheaConnected) {
              return left as MoleculeMix;
            }
            const out = new Set(left.filter((m) => m !== 'co2'));
            out.add('oxygen');
            return Array.from(out);
          },
        },
        {
          id: 's8-lungs-bottom-pulse',
          kind: 'receptacle',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    // ---------- Diaphragm (copy of Level 5 diaphragm, s8- prefix) ----------
    {
      id: 'diaphragm',
      art: 'diaphragm',
      cols: 3,
      rows: 2,
      ports: [
        {
          id: 's8-diaphragm-top',
          kind: 'spout',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
  ],
};
